import _ from 'lodash'
import moment from 'moment'
import { useFormik } from 'formik'
import { useSelector } from 'react-redux'
import { useState, useEffect } from 'react'

import { Colors } from '~/styles'
import { ExecutionAPI } from '~/api'
import {
  ConvertLocalTimeGet,
  ConvertLocalTimePost,
  getDurationBetweenTwoTime,
} from '~/utils'
import { Lookup } from '../../../../utils'
import { insertLog } from '../../../../utils/auditTrail'

const activityColorStatus = (tasks) => {
  const filteredTasks = tasks.filter(task => task.level_value !== 'Not Applicable')

  if (filteredTasks.some(task => task.is_red)) {
    return Colors.newRed
  }

  if (filteredTasks.some(task => task.is_orange)) {
    return Colors.newOrange
  }

  if (filteredTasks.every(task => task.is_green)) {
    return Colors.newGreen
  }

  return Colors.grey
}

export const isAllowedNextActivity = (tasks, progress) => {
  const allMandatory = tasks.every(task => task.level_value === 'Mandatory')
  if (allMandatory) {
    return progress === 100 && tasks.every(task => task.is_green === true)
  }

  const tolerableTasks = tasks.filter(task => task.level_value === 'Tolerable')
  if (tolerableTasks.length > 0) {
    for (const task of tolerableTasks) {
      if (task.is_red) return false

      if (task.is_orange) {
        const hasRemarks = task.remarks && task.remarks.trim() !== ''
        const hasAttachments = Array.isArray(task.progress_task_attachment_list) && task.progress_task_attachment_list.length >= 1
        if (!hasRemarks || !hasAttachments) return false
      }
    }

    if (progress !== 100) {
      return false
    }
  }

  return true
}

const mapActivitiesWithPermissions = (activities) => {
  return activities.map((activity, index) => {
    const isFirst = index === 0
    const current = activities[index]
    const previous = activities[index - 1]

    const is_task_completed = current.progress === 100
    const is_allowed_delete_task =
      activity.progress !== 100 && (isFirst || previous.progress === 100)
    const is_allowed_current_task = isFirst || previous.progress === 100

    return {
      ...activity,
      is_task_completed,
      is_allowed_delete_task,
      is_allowed_current_task,
    }
  })
}

const isTaskCompleted = (task) => {
  if (task.level_value === 'Mandatory' && task.is_green) {
    return true
  }

  if (task.level_value === 'Tolerable' && task.is_green) {
    return true
  }

  if (
    task.level_value === 'Tolerable' &&
    task.is_orange &&
    task.remarks && task.remarks.trim() !== '' &&
    Array.isArray(task.progress_task_attachment_list) &&
    task.progress_task_attachment_list.length >= 1
  ) {
    return true
  }

  if (task.level_value === 'Not Applicable') {
    return true
  }

  return false
}

const getCompletedTaskCount = (taskList) => {
  return taskList.reduce((count, task) => {
    return isTaskCompleted(task) ? count + 1 : count
  }, 0)
}

const createForm = (dataAll) => {
  const formatted = durationToFormattedString(dataAll.Forecast_Berth_Dur)
  const splitted = formatted.split(' ')
  const forecastBerthDurHour = parseInt(splitted[0])
  const forecastBerthDurMinute = parseInt(splitted[2])
  const forecastArrival = ConvertLocalTimeGet(
    dataAll.Location,
    dataAll.Forecast_Arrival,
    'YYYY-MM-DD HH:mm'
  )
  const forecastDepart = ConvertLocalTimeGet(
    dataAll.Location,
    dataAll.Forecast_Depart,
    'YYYY-MM-DD HH:mm'
  )

  return useFormik({
    enableReinitialize: true,
    initialValues: {
      Forecast_Arrival:
        dataAll.Forecast_Arrival === '' ? null : forecastArrival,
      Forecast_Depart: dataAll.Forecast_Depart === '' ? null : forecastDepart,
      Forecast_Berth_Dur: dataAll.Forecast_Berth_Dur,
      Forecast_Berth_Dur_Hour: forecastBerthDurHour,
      Forecast_Berth_Dur_Minute: forecastBerthDurMinute
    }
  })
}

const fetchActivityData = async (srCode) => {
  try {
    const response = await ExecutionAPI.getActivityList(srCode)
    return new Promise((resolve, reject) => {
      // set the activity carousel opened by default
      const activityData = response?.data?.message?.data || []
      const mappedActivityWithNextTaskPermission = mapActivitiesWithPermissions(activityData)
      const mappedActivity = mappedActivityWithNextTaskPermission.map((activity) => {
        /**
         * Maps over a list of task objects and applies conditional validation and notes:
         * - If a task is Tolerable
         * - If a task is marked as orange (`is_orange`), and both `remarks` is empty or missing and there are no attachments in `progress_task_attachment_list`,
         *   it sets `inEdit` to false and adds a `remarks_note` indicating that remarks and at least one document are required to proceed.
         * - If a task is marked as red (`is_red`), it sets `inEdit` to false and adds a `status_note` indicating that the task cannot proceed to the next activity.
         * - Otherwise, returns the task unchanged.
         *
         * @param {Array<Object>} tasks - The list of task objects to be validated and mapped.
         * @returns {Array<Object>} The new list of tasks with validation notes and flags applied as needed.
         */
        const tasks = activity.progress_task_header_list || []
        const mappedTaskList = tasks.map(task => {
          const isTolerableTask = String(task.level_value).toLowerCase() === 'tolerable'
          let status_note, remarks_note, attachment_note = ''

          if (isTolerableTask && task.is_orange && (!task.remarks && task.remarks.trim() === '')) {
          remarks_note = 'Must fill remarks to able to continue to next activity'
          }

          if (
            isTolerableTask && task.is_orange &&
            (!Array.isArray(task.progress_task_attachment_list) || task.progress_task_attachment_list.length === 0)
          ) {
            attachment_note = 'Must upload document min 1 to able to continue to next activity'
          }

          if (task.is_red) {
            status_note = 'Can\'t continue to next activity'
          }

          return {
            ...task,
            status_note,
            remarks_note,
            attachment_note,
          }
        })

        const completedTask = getCompletedTaskCount(activity.progress_task_header_list || [])

        return {
          ...activity,
          isOpen: true,
          inEdit: false,
          progress_task_counter: `(${completedTask}/${tasks.length})`,
          progress_task_header_list: mappedTaskList,
          color_status: activityColorStatus(mappedTaskList),
        }
      })

      resolve(mappedActivity)
      reject(new Error('Failed to fetch activity data'))
    })
  } catch (error) {
    return error
  }
}

const useFetchActivityBySRCode = (srCode, setIsLoading, setTotalPercentage) => {
  const [activityList, setActivityList] = useState([])

  const fetchData = async () => {
    setIsLoading(true)
    const activityData = await fetchActivityData(srCode)
    const totalPercentage = _.sumBy(activityData, (act) => act.percentage)

    setTotalPercentage(totalPercentage)
    setActivityList(activityData)
    setIsLoading(false)
  }

  useEffect(() => {
    if (srCode) {
      fetchData()
    }
  }, [srCode])

  return [activityList, setActivityList]
}

// HOF: higher-order-function
const handleOpenActivityDetailHOF = (activityList, setActivityList) => {
  return (activity) => {
    const updated = activityList.map((act) => {
      if (activity.id === act.id) return { ...act, isOpen: !act.isOpen }
      return act
    })

    setActivityList(updated)
  }
}

const insertNewTask = async (taskHeaderData) => {
  try {
    const response = await ExecutionAPI.insertProgressTaskHeader(taskHeaderData)
    return new Promise((resolve, reject) => {
      resolve(response.data.message.data)
      reject('Failed to insert task')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to insert task',
      action: Lookup.LOG_ACTION.INSERT,
      refNo: taskHeaderData?.sr_code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
    })
    // throw error to be caught by the source/first caller
    // You'll see similar approach in other functions
    throw new Error('Failed to insert task => ' + error.toString())
  }
}

const addNewTaskHOF = (setActivityList, location) => {
  return async (activity, taskDescription) => {
    try {
      const newTask = {
        percentage: 0,
        task: taskDescription,
        sr_code: activity.sr_code,
        activity_code: activity.code,
        created_date: ConvertLocalTimePost(location, new Date()),
        modified_date: ConvertLocalTimePost(location, new Date()),
        sort_no:
          findMaxSortNoTaskList(activity.progress_task_header_list || []) + 1
      }

      // As user add new task, the progress of the activity will adjust based on the already defined formula
      const updatedTaskHeaderList = [
        ...(activity.progress_task_header_list ?? []),
        newTask
      ]
      const { progress } = checkTasksPercentage(
        updatedTaskHeaderList,
        activity.percentage
      )
      const updatedActivity = {
        ...activity,
        progress: progress,
        progress_task_header_list: null
      }
      await updateActivityAndTaskHeader(updatedActivity, newTask)

      await insertNewTask(newTask)
      const result = await fetchActivityData(activity.sr_code)
      setActivityList(result)
    } catch (error) {
      return error
    }
  }
}

const deleteTask = async (taskId, modifiedDate) => {
  try {
    const response = await ExecutionAPI.deleteProgressTaskHeader(
      taskId,
      modifiedDate
    )
    new Promise((resolve, reject) => {
      resolve(response.data.message.data)
      reject('Failed to delete task')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to delete task',
      action: Lookup.LOG_ACTION.DELETE,
      refNo: taskId || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
    })
    throw new Error('Failed to delete task => ' + error.toString())
  }
}

const deleteTaskHOF = (
  setActivityList,
  location,
  activity,
  task,
  setToggleDelete,
  setShowAlert
) => {
  return async () => {
    try {
      // Remove out the will be deleted task and recalculate activity progress
      const filteredTaskHeaderList = (
        activity.progress_task_header_list ?? []
      ).filter((ts) => ts.code !== task.code)
      const { progress } = checkTasksPercentage(
        filteredTaskHeaderList,
        activity.percentage
      )
      const finishDate = findTheLatestDateFromTasksList(filteredTaskHeaderList)
      const willBeUpdatedActivity = {
        ...activity,
        progress: progress,
        finish_date: finishDate,
        progress_task_header_list: null, // progress_task_header_list is not used on the backend when delete task
        modified_date: ConvertLocalTimePost(location, new Date())
      }

      await deleteTask(task.id, ConvertLocalTimePost(location, new Date()))
      await updateActivityAndTaskHeader(willBeUpdatedActivity, task)
      const result = await fetchActivityData(activity.sr_code)
      setActivityList(result)
      if (result) {
        setShowAlert({
          msg: 'Delete data has been successful',
          success: true,
          visible: true,
          type: 'delete'
        })
        setToggleDelete(false)
      } else {
        setShowAlert({
          msg: 'Cannot delete data',
          success: false,
          visible: true,
          type: 'delete'
        })
        setToggleDelete(false)
      }
    } catch (error) {
      setToggleDelete(false)
      return error
    }
  }
}

const updateActivity = async (activity) => {
  try {
    const response = await ExecutionAPI.updateProgressActivity(activity)
    return new Promise((resolve, reject) => {
      resolve(response.data.message.data)
      reject('Failed to update activity and task header')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to update activity and task header',
      action: Lookup.LOG_ACTION.UPDATE,
      refNo: activity.sr_code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY,
    })
    throw new Error('Failed to update activity => ' + error.toString())
  }
}

const updateActivityHOF = (setActivityList) => {
  return async (activity) => {
    try {
      await updateActivity(activity)
      const result = await fetchActivityData(activity.sr_code)
      setActivityList(result)
    } catch (error) {
      return error
    }
  }
}

const updateActivityAndTaskHeader = async (
  activityPayload,
  taskHeaderPayload
) => {
  try {
    const response = await ExecutionAPI.updateActivityAndTaskHeader(
      activityPayload,
      taskHeaderPayload
    )
    return new Promise((resolve, reject) => {
      resolve(response.data.message.data)
      reject('Failed to update activity and task header')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to update activity and task header',
      action: Lookup.LOG_ACTION.UPDATE,
      refNo: activityPayload.sr_code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY,
    })
    throw new Error(
      'Failed to update activity and task header => ' + error.toString()
    )
  }
}

const updateActivityAndTaskHeaderHOF = (
  activityList,
  setActivityList,
  setIsLoading
) => {
  return async (activity, task) => {
    try {
      let updatedTask
      let updatedActivity

      activityList.forEach((act) => {
        if (activity.code === act.code) {
          const updatedTaskList = (act.progress_task_header_list || []).map(
            (ts) => {
              if (ts.code === task.code) {
                const actualDurTime = getDurationBetweenTwoTime(
                  task.actual_start_time,
                  task.actual_finish_time
                )
                updatedTask = {
                  ...ts,
                  ...task,
                  actual_dur_time: actualDurTime,
                  inEdit: false
                }
                return updatedTask
              }

              return ts
            }
          )

          updatedActivity = {
            ...act,
            ...activity,
            progress_task_header_list: updatedTaskList
          }
          return updatedActivity
        }

        return act
      })

      // Set progress_task_header_list to null to enhance the marshaling performance on the Backend
      setIsLoading(true)
      await updateActivityAndTaskHeader(
        { ...updatedActivity, progress_task_header_list: null },
        updatedTask
      )
      const result = await fetchActivityData(activity.sr_code)
      setActivityList(result)
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      return error
    }
  }
}

const formatDatetime = (datetime) => {
  if (!datetime) {
    // if datetime is a falsy return empty string
    return ''
  }
  return moment(datetime).format('DD MMMM YYYY HH:mm')
}

const validateWhenCheckActivity = async (dataSrCode, dataPlanArrival) => {
  const response = await ExecutionAPI.getConfirmation(dataSrCode)
  if (response.data && response.data.data) {
    if (
      response.data.data.Count_Total === 0 ||
      response.data.data.Count_Total - response.data.data.Count_False !==
        response.data.data.Count_Total ||
      moment(dataPlanArrival).diff(
        moment(new Date()).toISOString(),
        'minutes',
        true
      ) > 120
    ) {
      return new Promise((resolve, reject) => {
        resolve(false)
        reject(new Error('Failed to validate when check activity'))
      })
    }
  }

  return new Promise((resolve, reject) => {
    resolve(true)
    reject(new Error('Failed to validate when check activity'))
  })
}

const handleCheckProgressActivityHOF = (
  dataAll,
  dataSrCode,
  isInProgress,
  isTabSRValid,
  setShowAlert,
  setNotConfirm,
  setActivityList,
  dataPlanArrival,
  setOpenWarning,
  getDataExecution
) => {
  return async (activityData) => {
    try {
      if (!isTabSRValid) {
        setShowAlert({
          msg: 'Please fill in the required fields on SR Tab',
          success: false,
          visible: true,
          type: 'insert'
        })

        return
      }

      // validate only vessel alongside activity
      if (String(dataAll.Sr_Type).toLowerCase().includes('vessel alongside')) {
        const isInProgress = await validateIsInProgressActivity(activityData.sr_code)
        if (isInProgress > 0) {
          setShowAlert({
            msg: 'The vessel is already in progress, or there is a vessel currently berthed at the port. ',
            type: 'error',
            success: false,
            visible: true,
          })
          return
        }
      }

      if (
        isInProgress &&
        dataAll.Sr_Status !== 'In Progress' &&
        String(dataAll.Sr_Type).toLowerCase().includes('vessel alongside')
      ) {
        setOpenWarning(true)
        return
      }

      const validToCheck = await validateWhenCheckActivity(
        dataSrCode,
        dataPlanArrival
      )
      if (dataAll.Sr_Status !== 'In Progress' && !validToCheck) {
        setNotConfirm(true)
        setOpenWarning(true)
        return
      }

      await ExecutionAPI.handleCheckProgressActivity(activityData)
      const result = await fetchActivityData(activityData.sr_code)
      setActivityList(result)
      getDataExecution()
    } catch (error) {
      return error
    }
  }
}

const toggleEditModeHOF = (activityList, setActivityList, setAttachmentList) => {
  return (activity, task) => {
    const updatedActivityList = activityList.map((act) => {
      if (activity.code === act.code) {
        const updatedTaskList = (act.progress_task_header_list || []).map(
          (ts) => {
            if (task.code === ts.code)
              return { ...ts, inEdit: !(ts.inEdit ?? false) }
            return ts
          }
        )

        return { ...act, progress_task_header_list: updatedTaskList }
      }

      return act
    })
    setAttachmentList(task?.progress_task_attachment_list || null)
    setActivityList(updatedActivityList)
  }
}

const checkTasksPercentage = (tasks, activityPercentage) => {
  // E.g: two tasks with percentage x and y
  // The formula is progress = (((x + y)/200) * activityPercentage)

  const maxPercentage = 100 * (tasks ?? []).length // Obviously, 100% is the max percentage
  const percentagesSum = _.sum(tasks.map((task) => task.percentage ?? 0))
  return {
    isExceed100: percentagesSum > maxPercentage, // True if sum of task's percentage is greater than 100% * number of tasks
    progress: (percentagesSum / maxPercentage) * activityPercentage // Calculate progress
  }
}

const findTheLatestDateFromTasksList = (tasks) => {
  // Since there is no tasks in the activity, then make the finish time null
  if (tasks.length === 0) {
    return null
  }

  // Find the latest actual finish time in a list of progress task headers
  const taskDateList = tasks.map((task) => new Date(task.actual_finish_time))
  const finishDate = moment(_.max(taskDateList)).format()

  return finishDate !== '1970-01-01T07:00:00+07:00' ? finishDate : null
}

const findMaxSortNoTaskList = (tasks) => {
  // Find the max sort number in a list of progress task headers
  const taskSortList = tasks.map((task) => task.sort_no)
  const maxSortNo = _.max(taskSortList)

  return maxSortNo || 0
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const updateTaskHeaderSortNo = async (taskHeaderList) => {
  try {
    const response = await ExecutionAPI.updateTaskHeaderSortNo(taskHeaderList)
    return new Promise((resolve, reject) => {
      resolve(response?.data?.message?.data ?? false)
      reject(new Error('Failed to update task header sort no'))
    })
  } catch (error) {
    return error
  }
}

const onDragEndHOF = (activityList, setActivityList) => {
  return async (result, activity) => {
    try {
      if (!result.destination) {
        return
      }

      if (result.destination.index === result.source.index) {
        return
      }

      const items = reorder(
        activity.progress_task_header_list || [],
        result.source.index,
        result.destination.index
      ).map((item, index) => ({ ...item, sort_no: index }))
      const updatedActivity = activityList.map((act) => {
        if (activity.id === act.id)
          return { ...act, progress_task_header_list: items }
        return act
      })

      setActivityList(updatedActivity)
      await updateTaskHeaderSortNo(items)
    } catch (error) {
      return error
    }
  }
}

const durationToFormattedString = (duration) => {
  return parseInt(duration)
    ? `${Math.floor(duration / 60)} Hours ${duration % 60} Minutes`
    : '0 Hours 0 Minutes'
}

const toggleModalHOF = (modalStatus, setActivityList, setShowModalStatus) => {
  return async (activityCode, srCode, taskCode, taskName) => {
    setShowModalStatus((prev) => {
      return {
        ...prev,
        taskCode,
        taskName,
        activityCode,
        isOpen: !prev.isOpen
      }
    })

    if (!modalStatus.isOpen === false) {
      // Fetch the activity data based on the activity code
      // This is to ensure that the activity data is up-to-date when the modal is closed
      const response_fetch_activity = await fetchActivityData(srCode)
      setActivityList(response_fetch_activity)
    }
  }
}

const checkIsInProgress = async (codeVessel, setIsInProgress) => {
  try {
    const { Location } = useSelector((state) => state.usersProfileReducer)

    const { LocationDescription } = Location

    const result = await ExecutionAPI.getAllExecutions(
      0,
      1000,
      LocationDescription,
      `[{"type": "text", "field" : "Vessel_Code", "value": "${codeVessel}"},{"type": "text", "field" : "Sr_Status", "value": "In Progress"}]`
    )
    if (result.data) {
      if (result.data.count > 0) {
        setIsInProgress(true)
      } else {
        setIsInProgress(false)
      }
    }
  } catch (err) {
    return err
  }
}

const updateSort = async ({ dataSort, data, handleClose, setActivityList }) => {
  try {
    const sort = []
    dataSort.forEach((item) => {
      sort.push({ Field: item.field, Dir: item.dir })
    })
    const response = await ExecutionAPI.updateTaskHeaderSort(sort, data)
    if (response.data) {
      const result = await fetchActivityData(data.sr_code)
      setActivityList(result)
      handleClose()
    }
  } catch (err) {
    console.log(err)
    handleClose()
  }
}

const deleteChecklistTask = async (payload) => {
  try {
    const response = await ExecutionAPI.DeleteTaskActivityChecklist(payload)
    return new Promise((resolve, reject) => {
      resolve(response.data.status)
      reject('Failed to delete task')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to delete task',
      action: Lookup.LOG_ACTION.DELETE,
      refNo: payload.code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
    })
    throw new Error('Failed to delete task => ' + error.toString())
  }
}

const isLastActivity = (activity, activityList) => {
  const lastActivity = activityList[activityList.length - 1]
  return activity.code === lastActivity.code
}

const deleteChecklistTaskHOF = (
  setActivityList,
  location,
  activity,
  task,
  activityList,
  setToggleDelete,
  setShowAlert
) => {
  return async () => {
    try {
      const payload = {
        id: task.id,
        code: task.code,
        sr_code: task.sr_code,
        activity_code: activity.code,
        modified_date: ConvertLocalTimePost(location, new Date()),
        is_last_activity: isLastActivity(activity, activityList),
      }

      const response_delete_task = await deleteChecklistTask(payload)
      const response_fetch_activity = await fetchActivityData(activity.sr_code)

      setActivityList(response_fetch_activity)

      if (response_delete_task === 200) {
        setShowAlert({
          msg: 'Task has been deleted successfully',
          type: 'delete',
          success: true,
          visible: true,
        })
        setToggleDelete(false)
      } else {
        setShowAlert({
          msg: 'Failed to delete task',
          type: 'delete',
          success: false,
          visible: true,
        })
        setToggleDelete(false)
      }
    } catch (error) {
      setToggleDelete(false)
      return error
    }
  }
}

const updateActivityChecklistTask = async (payload) => {
  try {
    const response = await ExecutionAPI.UpdateTaskActivityChecklist(payload)
    return new Promise((resolve, reject) => {
      resolve(response.data.status)
      reject('Failed to update activity and task header')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to update activity and task header',
      action: Lookup.LOG_ACTION.UPDATE,
      refNo: payload.code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY,
    })
    return error
  }
}

const uploadAttachmentActivityChecklistTask = async (payload) => {
  try {
    const response = await ExecutionAPI.uploadMultipleAttachment(payload)
    return new Promise((resolve, reject) => {
      resolve(response)
      reject('Failed to upload attachment activity checklist task')
    })
  } catch (error) {
    insertLog({
      message: error?.message || 'Failed to upload attachment activity checklist task',
      action: Lookup.LOG_ACTION.UPLOAD,
      refNo: payload.code || '',
      module: Lookup.LOG_MODULE.EXECUTION,
      submodule: Lookup.LOG_SUB_MODULE.ACTIVITY_TASK,
    })
    return error
  }
}

const validateIsInProgressActivity = async (srCode) => {
  try {
    const response = await ExecutionAPI.getInProgressServiceRequest(srCode)
    if (response.data) {
      return response.data.count
    }
  } catch (error) {
    return error
  }
}

const updateActivityChecklistTaskHOF = (
  dataAll,
  location,
  activityList,
  attachmentList,
  setActivityList,
  setShowAlert,
  setIsLoading,
  setAttachmentList,
) => {
  return async (activity, payload) => {
    const hasAttachment = attachmentList?.filter((item) => !item.is_deleted)
    // Validate if status is orange, remarks and Attachment are required
    if (payload.is_orange && (!payload.remarks || (!attachmentList || !hasAttachment?.length))) {
      setShowAlert({
        msg: 'Must fill remarks, and upload document min 1 to able to continue to next activity',
        type: 'error',
        success: false,
        visible: true,
      })
    }
    if (payload.is_red) {
      setShowAlert({
        msg: 'Can\'t continue to next activity',
        type: 'error',
        success: false,
        visible: true,
      })
    }

    // validate only vessel alongside activity
    if (activity.is_berthing && String(dataAll.Sr_Type).toLowerCase().includes('vessel alongside')) {
      const isInProgress = await validateIsInProgressActivity(activity.sr_code)
      if (isInProgress > 0) {
        setShowAlert({
          msg: 'The vessel is already in progress, or there is a vessel currently berthed at the port. ',
          type: 'error',
          success: false,
          visible: true,
        })

        // if the activity is in progress, we will not update the activity checklist task
        // instead we will fetch the activity data again to refresh the activity list
        const response_fetch_activity = await fetchActivityData(activity.sr_code)
        setActivityList(response_fetch_activity)
        return
      }
    }

    const formData = new FormData()

    const filterNewAttachmentList = attachmentList ? attachmentList.filter((item) => item.is_new === true) : []
    filterNewAttachmentList.forEach((item) => formData.append('file', item.file))

    if (filterNewAttachmentList.length > 0) {
      try {
        // handle update with upload file
        setIsLoading(true)
        const response = await uploadAttachmentActivityChecklistTask(formData)

        if (response.status === 200) {
          // map the response to the payload
          const newAttachmentList = response.data.data.map((item) => ({
            path: item.Path,
            filename: item.OriginFileName,
            filesize: item.FileSize,
            attachment_type: item.FileType,
            created_date: ConvertLocalTimePost(location, new Date()),
            modified_date: ConvertLocalTimePost(location, new Date()),
          }))

          const mappedAttachmentList = attachmentList
            ? [...newAttachmentList, ...attachmentList]
            : newAttachmentList

          const mappedPayload = {
            ...payload,
            is_last_activity: isLastActivity(activity, activityList),
            progress_task_attachment_list: mappedAttachmentList,
          }

          const response_update_task = await updateActivityChecklistTask(mappedPayload)
          const response_fetch_activity = await fetchActivityData(activity.sr_code)
          setActivityList(response_fetch_activity)
          if (response_update_task === 200) setIsLoading(false)
          throw new Error('Failed to update activity checklist task')
        }
        throw new Error('Failed to upload attachment activity checklist task')
      } catch (error) {
        setIsLoading(false)
        setAttachmentList(null)
        return error
      }
    } else {
      // handle update without upload file
      try {
        // Set progress_task_header_list to null to enhance the marshaling performance on the Backend
        setIsLoading(true)
        const response_update_task = await updateActivityChecklistTask({
          ...payload,
          is_last_activity: isLastActivity(activity, activityList),
        })

        const response_fetch_activity = await fetchActivityData(activity.sr_code)
        setActivityList(response_fetch_activity)

        if (response_update_task === 200) {
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        return error
      }
    }
  }
}

const createActivityChecklistTask = async (payload) => {
  try {
    const response = await ExecutionAPI.InsertTaskActivityChecklist(payload)

    return new Promise((resolve, reject) => {
      resolve(response.data.status)
      reject('Failed to update activity and task header')
    })
  } catch (error) {
    return error
  }
}

const createActivityChecklistTaskHOF = (
  activityList,
  setActivityList,
  setShowAlert,
  toggleModalTask,
) => {
  return async (activity, payload) => {
    try {
      const response_create_task = await createActivityChecklistTask({
        ...payload,
        is_last_activity: isLastActivity(activity, activityList),
      })

      const response_fetch_activity = await fetchActivityData(activity.sr_code)
      setActivityList(response_fetch_activity)

      if (response_create_task === 200) {
        toggleModalTask()
        setShowAlert({
          msg: 'Task has been added successfully',
          type: 'success',
          success: true,
          visible: true,
        })
      }
    } catch (error) {
      setShowAlert({
        msg: 'Failed to add task',
        type: 'error',
        success: false,
        visible: true,
      })
    }
  }
}

export default {
  createForm,
  updateSort,
  onDragEndHOF,
  addNewTaskHOF,
  deleteTaskHOF,
  formatDatetime,
  toggleModalHOF,
  checkIsInProgress,
  toggleEditModeHOF,
  updateActivityHOF,
  checkTasksPercentage,
  deleteChecklistTaskHOF,
  useFetchActivityBySRCode,
  handleOpenActivityDetailHOF,
  updateActivityAndTaskHeaderHOF,
  handleCheckProgressActivityHOF,
  updateActivityChecklistTaskHOF,
  createActivityChecklistTaskHOF,
  findTheLatestDateFromTasksList
}
